/**
 * Serialization between the in-memory office models (`Workbook`, `Presentation`)
 * and the single PowerSync `block` row that persists them local-first.
 *
 * A document is one `page` + one content `block` whose `id === page.id` and
 * whose `type` is the document kind (`workbook` / `deck`). The whole model is
 * JSON-serialized into `block.content`; `block.text` is a plaintext projection
 * used for search and for the version-history word diff. Keeping the block id
 * equal to the page id makes autosave (`syncPageBlocksMutation`, diff-by-id) a
 * stable single-row upsert that never churns.
 */
import type { Block } from "@folio/powersync";
import {
  DECK_BLOCK_TYPE,
  type EditorBlockNode,
  isSidecarBlockType,
  WORKBOOK_BLOCK_TYPE,
} from "@folio/shared";
import type { Cell, Presentation, Workbook } from "./office-io";
import { emptyWorkbook } from "./workbook-ops";
import { emptyPresentation } from "./presentation-ops";

// ── Plaintext projections (search + version diff) ───────────────────────────

function cellText(cell: Cell | undefined): string {
  if (!cell) {
    return "";
  }
  if (cell.formula) {
    return `=${cell.formula}`;
  }
  const v = cell.value;
  if (v.t === "number") {
    return String(v.v);
  }
  if (v.t === "bool") {
    return v.v ? "TRUE" : "FALSE";
  }
  if (v.t === "text") {
    return v.v;
  }
  return "";
}

export function workbookPlainText(workbook: Workbook): string {
  return workbook.sheets
    .map((sheet) => {
      const cells = sheet.rows
        .flatMap((row) => row.map((cell) => cellText(cell)))
        .filter((text) => text.length > 0)
        .join(" ");
      return `${sheet.name} ${cells}`.trim();
    })
    .filter((line) => line.length > 0)
    .join("\n");
}

export function presentationPlainText(deck: Presentation): string {
  return deck.slides
    .map((slide) =>
      [slide.title ?? "", ...slide.body].filter((line) => line.trim() !== "").join("\n"),
    )
    .filter((block) => block.length > 0)
    .join("\n\n");
}

// ── Workbook ↔ content block ────────────────────────────────────────────────

export function workbookToBlockNode(pageId: string, workbook: Workbook): EditorBlockNode {
  return {
    id: pageId,
    type: WORKBOOK_BLOCK_TYPE,
    content: JSON.stringify(workbook),
    text: workbookPlainText(workbook),
  };
}

function isWorkbook(value: unknown): value is Workbook {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as { sheets?: unknown }).sheets)
  );
}

/**
 * Parses the content block of a workbook document back into a `Workbook`.
 * Corrupt/absent content falls back to an empty workbook so the editor always
 * mounts (never hangs on a bad row).
 */
export function blockToWorkbook(block: Block | undefined): Workbook {
  if (!block?.content) {
    return emptyWorkbook();
  }
  try {
    const parsed: unknown = JSON.parse(block.content);
    if (isWorkbook(parsed) && parsed.sheets.length > 0) {
      return parsed;
    }
  } catch {
    // fall through to empty
  }
  return emptyWorkbook();
}

// ── Presentation ↔ content block ────────────────────────────────────────────

export function presentationToBlockNode(pageId: string, deck: Presentation): EditorBlockNode {
  return {
    id: pageId,
    type: DECK_BLOCK_TYPE,
    content: JSON.stringify(deck),
    text: presentationPlainText(deck),
  };
}

function isPresentation(value: unknown): value is Presentation {
  return (
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as { slides?: unknown }).slides)
  );
}

export function blockToPresentation(block: Block | undefined): Presentation {
  if (!block?.content) {
    return emptyPresentation();
  }
  try {
    const parsed: unknown = JSON.parse(block.content);
    if (isPresentation(parsed) && parsed.slides.length > 0) {
      return parsed;
    }
  } catch {
    // fall through to empty
  }
  return emptyPresentation();
}

/**
 * Picks the single content block from a page's block rows (there is exactly one
 * for a document; its id equals the page id). Tolerant of extra rows.
 */
export function pickContentBlock(blocks: Block[], pageId: string): Block | undefined {
  return (
    blocks.find((row) => row.id === pageId) ??
    // Never fall back to a SIDECAR row (comment thread): it is not content.
    blocks.find((row) => !isSidecarBlockType(row.type))
  );
}
